import { Mutex } from 'async-mutex'
import axios, { AxiosRequestConfig } from 'axios'

import { captureException } from '@sentry/nextjs'

import appConfig from '~/config/app'
import { RefreshTokenResponse, FirebaseCustomTokenResponse, ApiResponse } from '~/redux/apis/types'
import { trimObjectProperties } from '~/utils/helpers'

import authService from './auth.service'

// create a new mutex
const mutex = new Mutex()

interface AxiosSuccessReturn<T> {
  data: T
  meta?: { request: Request; response: Response }
}
interface AxiosErrorReturn {
  error: {
    type: 'error'
    status: number
    data: any
  }
  data?: undefined
  meta?: { request: Request; response: Response }
}

const requestHandler = <T extends AxiosRequestConfig>(request: T): T => {
  const accessToken = authService.getAccessToken()

  if (accessToken) {
    request.headers.Authorization = `Bearer ${accessToken}`
  }

  if (request.data) {
    request.data = trimObjectProperties(request.data, appConfig.skipTrimmingProps)
  }
  return request
}

// const errorHandler = (error: AxiosError): Promise<AxiosErrorReturn> => {
//   return Promise.reject(error.response)
// }

export const axiosInstance = axios.create({
  baseURL: `${appConfig.homApiUrl}/api`,
  withCredentials: true,
  validateStatus(status) {
    return status >= 200 && status < 300 // default
  },
})

axiosInstance.interceptors.request.use(
  (request) => requestHandler(request),
  // (error) => errorHandler(error),
)

export async function callApi<T>(reqObj: AxiosRequestConfig): Promise<AxiosSuccessReturn<T> | AxiosErrorReturn> {
  await mutex.waitForUnlock()
  try {
    const response = await axiosInstance(reqObj)
    return { data: response.data }
  } catch (err) {
    const errResponse = err.response
    if (!errResponse) {
      captureException(err, { tags: { feature: 'api', action: 'network-error' } })
    } else if (errResponse.status === 401 && errResponse.data && errResponse.data.message === 'invalid token') {
      // checking whether the mutex is locked
      if (!mutex.isLocked()) {
        const release = await mutex.acquire()
        try {
          const refreshResult = await axiosInstance({
            url: '/v1/auth/refresh',
            method: 'POST',
          })

          if (refreshResult.data) {
            authService.setSession(refreshResult.data)

            const result = await axiosInstance(reqObj)
            return { data: result.data }
          } else {
            authService.setSession(null)
            return {
              error: {
                type: errResponse.type || 'error',
                status: errResponse.status,
                data: errResponse.data,
              },
            }
          }
        } finally {
          // release must be called once the mutex should be released again.
          release()
        }
      } else {
        // wait until the mutex is available without locking it
        await mutex.waitForUnlock()
        const result = await axiosInstance(reqObj)
        return { data: result.data }
      }
    } else {
      return {
        error: {
          type: errResponse.type || 'error',
          status: errResponse.status,
          data: errResponse.data,
        },
      }
    }
  }
}

export function refreshToken() {
  return callApi<ApiResponse<RefreshTokenResponse>>({
    url: '/v1/auth/refresh',
    method: 'POST',
  })
}

type RefreshFirebaseTokenType = Promise<AxiosErrorReturn | AxiosSuccessReturn<ApiResponse<FirebaseCustomTokenResponse>>>

export function refreshFirebaseToken(): RefreshFirebaseTokenType {
  return callApi<ApiResponse<FirebaseCustomTokenResponse>>({
    url: '/v1/auth/get-firebase-token',
    method: 'GET',
  })
}

// server side axios client for HOM-API calls, shouldn't be used for client side calls
export async function callApiFromServerSide<T>(
  reqObj: AxiosRequestConfig,
): Promise<AxiosSuccessReturn<T> | AxiosErrorReturn> {
  try {
    const response = await axiosInstance(reqObj)
    return { data: response.data }
  } catch (err) {
    const errResponse = err.response
    return {
      error: {
        type: errResponse.type || 'error',
        status: errResponse.status,
        data: errResponse.data,
      },
    }
  }
}
